import * as THREE from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import { projectRoundedBoxUvs } from "./materials";
import type { CityTextures } from "./textures";

export type BuildingArchetype = "low-rise" | "mid-rise" | "tower";
export type BuildingMaterialRole = "brick" | "stone" | "glass" | "roof" | "metal";
export type BuildingSection = "body" | "podium" | "upper" | "lower-tower" | "upper-tower" | "crown";

export interface BuildingModelInput {
  x: number;
  z: number;
  width: number;
  depth: number;
  height: number;
  footprint: Float32Array;
  footprintStart: number;
  footprintEnd: number;
}

export interface BuildingPart {
  kind: "footprint" | "box" | "gable" | "facade";
  role: BuildingMaterialRole;
  section: BuildingSection;
  center: readonly [number, number, number];
  size: readonly [number, number, number];
  yawRadians: number;
  footprintScale?: number;
}

export interface BuildingComposition {
  archetype: BuildingArchetype;
  variant: number;
  parts: readonly BuildingPart[];
}

export interface BuildingRenderStats {
  buildings: number;
  parts: number;
  meshes: number;
  roles: Readonly<Record<BuildingMaterialRole, number>>;
}

export interface BuildingTileModel {
  root: THREE.Group;
  stats: BuildingRenderStats;
}

type Point = readonly [x: number, z: number];
const normalizedFootprints = new WeakMap<BuildingModelInput, Point[]>();

export function buildingSeedForPosition(x: number, z: number): number {
  let hash = Math.imul(Math.round(x * 10), 73_856_093);
  hash ^= Math.imul(Math.round(z * 10), 19_349_663);
  hash ^= hash >>> 16;
  return hash >>> 0;
}

export function buildingVariantForPosition(x: number, z: number): number {
  return buildingSeedForPosition(x, z) % 5;
}

function rectangle(input: BuildingModelInput): Point[] {
  const halfWidth = input.width / 2;
  const halfDepth = input.depth / 2;
  return [
    [input.x - halfWidth, input.z - halfDepth],
    [input.x + halfWidth, input.z - halfDepth],
    [input.x + halfWidth, input.z + halfDepth],
    [input.x - halfWidth, input.z + halfDepth],
  ];
}

function signedArea(points: readonly Point[]): number {
  let twiceArea = 0;
  for (let index = 0; index < points.length; index += 1) {
    const current = points[index]!;
    const next = points[(index + 1) % points.length]!;
    twiceArea += current[0] * next[1] - next[0] * current[1];
  }
  return twiceArea / 2;
}

function orientation(a: Point, b: Point, c: Point): number {
  return (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
}

function segmentsCross(a: Point, b: Point, c: Point, d: Point): boolean {
  return (
    orientation(a, b, c) * orientation(a, b, d) < 0 &&
    orientation(c, d, a) * orientation(c, d, b) < 0
  );
}

function isSimple(points: readonly Point[]): boolean {
  for (let left = 0; left < points.length; left += 1) {
    const leftNext = (left + 1) % points.length;
    for (let right = left + 1; right < points.length; right += 1) {
      const rightNext = (right + 1) % points.length;
      if (left === right || leftNext === right || rightNext === left) continue;
      if (segmentsCross(points[left]!, points[leftNext]!, points[right]!, points[rightNext]!))
        return false;
    }
  }
  return true;
}

function normalizedFootprint(input: BuildingModelInput): Point[] {
  const cached = normalizedFootprints.get(input);
  if (cached) return cached;
  const points: Point[] = [];
  const minX = input.x - input.width / 2;
  const maxX = input.x + input.width / 2;
  const minZ = input.z - input.depth / 2;
  const maxZ = input.z + input.depth / 2;
  for (let index = input.footprintStart; index < input.footprintEnd; index += 1) {
    const x = input.footprint[index * 2];
    const z = input.footprint[index * 2 + 1];
    if (x === undefined || z === undefined || !Number.isFinite(x) || !Number.isFinite(z)) {
      const fallback = rectangle(input);
      normalizedFootprints.set(input, fallback);
      return fallback;
    }
    const point: Point = [
      THREE.MathUtils.clamp(x, minX, maxX),
      THREE.MathUtils.clamp(z, minZ, maxZ),
    ];
    const previous = points.at(-1);
    if (!previous || Math.hypot(point[0] - previous[0], point[1] - previous[1]) > 1e-4)
      points.push(point);
  }
  if (
    points.length > 2 &&
    Math.hypot(points[0]![0] - points.at(-1)![0], points[0]![1] - points.at(-1)![1]) < 1e-4
  )
    points.pop();
  if (points.length < 3 || Math.abs(signedArea(points)) < 0.01 || !isSimple(points)) {
    const fallback = rectangle(input);
    normalizedFootprints.set(input, fallback);
    return fallback;
  }
  const contour = points.map(([x, z]) => new THREE.Vector2(x, -z));
  try {
    if (THREE.ShapeUtils.triangulateShape(contour, []).length === 0) {
      const fallback = rectangle(input);
      normalizedFootprints.set(input, fallback);
      return fallback;
    }
  } catch {
    const fallback = rectangle(input);
    normalizedFootprints.set(input, fallback);
    return fallback;
  }
  normalizedFootprints.set(input, points);
  return points;
}

function scaledFootprint(
  points: readonly Point[],
  input: BuildingModelInput,
  scale: number,
): Point[] {
  return points.map(([x, z]) => [input.x + (x - input.x) * scale, input.z + (z - input.z) * scale]);
}

function part(
  kind: BuildingPart["kind"],
  role: BuildingMaterialRole,
  section: BuildingSection,
  center: BuildingPart["center"],
  size: BuildingPart["size"],
  yawRadians = 0,
  footprintScale?: number,
): BuildingPart {
  return footprintScale === undefined
    ? { kind, role, section, center, size, yawRadians }
    : { kind, role, section, center, size, yawRadians, footprintScale };
}

function addCap(
  parts: BuildingPart[],
  input: BuildingModelInput,
  section: BuildingSection,
  scale: number,
  bottom: number,
): void {
  parts.push(
    part(
      "footprint",
      "roof",
      section,
      [input.x, bottom + 0.1, input.z],
      [input.width * scale, 0.2, input.depth * scale],
      0,
      scale,
    ),
  );
}

function addFacadeAssembly(
  parts: BuildingPart[],
  input: BuildingModelInput,
  source: readonly Point[],
  section: BuildingSection,
  outerScale: number,
  bottom: number,
  top: number,
  coreRole: BuildingMaterialRole,
  pierRole: "brick" | "stone",
): void {
  const height = top - bottom;
  const coreScale = outerScale * 0.9;
  parts.push(
    part(
      "footprint",
      coreRole,
      section,
      [input.x, bottom + height / 2, input.z],
      [input.width * coreScale, height, input.depth * coreScale],
      0,
      coreScale,
    ),
  );
  const outline = scaledFootprint(source, input, outerScale);
  const glassHeight = height - 1.6;
  for (let index = 0; index < outline.length; index += 1) {
    const start = outline[index]!;
    const end = outline[(index + 1) % outline.length]!;
    const deltaX = end[0] - start[0];
    const deltaZ = end[1] - start[1];
    const edgeLength = Math.hypot(deltaX, deltaZ);
    if (edgeLength < 1e-4) continue;
    const tangentX = deltaX / edgeLength;
    const tangentZ = deltaZ / edgeLength;
    const midpointX = (start[0] + end[0]) / 2;
    const midpointZ = (start[1] + end[1]) / 2;
    const towardCenterX = input.x - midpointX;
    const towardCenterZ = input.z - midpointZ;
    const towardCenterLength = Math.hypot(towardCenterX, towardCenterZ) || 1;
    const inwardX = towardCenterX / towardCenterLength;
    const inwardZ = towardCenterZ / towardCenterLength;
    const yaw = -Math.atan2(deltaZ, deltaX);
    if (edgeLength < 2 || glassHeight < 0.5) {
      parts.push(
        part(
          "facade",
          pierRole,
          section,
          [midpointX + inwardX * 0.14, bottom + height / 2, midpointZ + inwardZ * 0.14],
          [edgeLength, height, 0.28],
          yaw,
        ),
      );
      continue;
    }
    parts.push(
      part(
        "facade",
        "glass",
        section,
        [midpointX + inwardX * 0.04, bottom + 1 + glassHeight / 2, midpointZ + inwardZ * 0.04],
        [edgeLength - 1, glassHeight, 0.06],
        yaw,
      ),
    );
    for (const [endpoint, direction] of [
      [start, 1],
      [end, -1],
    ] as const) {
      parts.push(
        part(
          "box",
          pierRole,
          section,
          [
            endpoint[0] + tangentX * 0.225 * direction + inwardX * 0.14,
            bottom + height / 2,
            endpoint[1] + tangentZ * 0.225 * direction + inwardZ * 0.14,
          ],
          [0.45, height, 0.28],
          yaw,
        ),
      );
    }
  }
}

export function deriveBuildingComposition(input: BuildingModelInput): BuildingComposition {
  const source = normalizedFootprint(input);
  const variant = buildingVariantForPosition(input.x, input.z);
  const parts: BuildingPart[] = [];
  const rectangularity = Math.min(1, Math.abs(signedArea(source)) / (input.width * input.depth));

  if (input.height < 24) {
    const gable = rectangularity >= 0.72 && variant % 2 === 0;
    const roofHeight = gable ? THREE.MathUtils.clamp(input.height * 0.18, 1.2, 3.2) : 1.2;
    const bodyTop = input.height - roofHeight;
    const masonry = variant === 4 ? "stone" : "brick";
    addFacadeAssembly(parts, input, source, "body", 1, 0, bodyTop, masonry, masonry);
    if (gable) {
      parts.push(
        part(
          "gable",
          "roof",
          "body",
          [input.x, bodyTop + roofHeight / 2, input.z],
          [input.width, roofHeight, input.depth],
        ),
      );
    } else {
      addCap(parts, input, "body", 1, input.height - 1.2);
      parts.push(
        part(
          "box",
          "metal",
          "body",
          [input.x, input.height - 0.6, input.z],
          [input.width * 0.22, 0.8, input.depth * 0.22],
        ),
      );
      addCap(parts, input, "body", 0.22, input.height - 0.2);
    }
    return { archetype: "low-rise", variant, parts };
  }

  if (input.height < 60 || input.width < 8 || input.depth < 8) {
    const podiumHeight = THREE.MathUtils.clamp(input.height * 0.24, 6.4, 12);
    const upperScale = variant % 2 === 0 ? 0.88 : 0.82;
    const podiumRole = variant < 2 ? "brick" : "stone";
    addFacadeAssembly(parts, input, source, "podium", 1, 0, podiumHeight, podiumRole, podiumRole);
    addCap(parts, input, "podium", 1, podiumHeight - 0.2);
    addFacadeAssembly(
      parts,
      input,
      source,
      "upper",
      upperScale,
      podiumHeight,
      input.height - 2.2,
      "stone",
      "stone",
    );
    addCap(parts, input, "upper", upperScale, input.height - 2.2);
    parts.push(
      part(
        "box",
        "metal",
        "crown",
        [input.x, input.height - 1.1, input.z],
        [input.width * upperScale * 0.28, 1.8, input.depth * upperScale * 0.28],
      ),
    );
    addCap(parts, input, "crown", upperScale * 0.28, input.height - 0.2);
    return { archetype: "mid-rise", variant, parts };
  }

  const podiumHeight = THREE.MathUtils.clamp(input.height * 0.16, 8, 15);
  const crownHeight = THREE.MathUtils.clamp(input.height * 0.06, 3, 8);
  const towerHeight = input.height - podiumHeight - crownHeight;
  const lowerScale = 0.78 - 0.03 * (variant % 3);
  const split = input.height >= 100;
  const lowerHeight = split ? towerHeight * 0.6 : towerHeight;
  const towerTop = podiumHeight + towerHeight;
  addFacadeAssembly(parts, input, source, "podium", 1, 0, podiumHeight, "stone", "stone");
  addCap(parts, input, "podium", 1, podiumHeight - 0.2);
  addFacadeAssembly(
    parts,
    input,
    source,
    "lower-tower",
    lowerScale,
    podiumHeight,
    podiumHeight + lowerHeight,
    "glass",
    "stone",
  );
  parts.push(
    part(
      "footprint",
      "stone",
      "lower-tower",
      [input.x, podiumHeight + towerHeight / 2, input.z],
      [input.width * lowerScale * 0.42, towerHeight, input.depth * lowerScale * 0.42],
      0,
      lowerScale * 0.42,
    ),
  );
  if (split) {
    const upperScale = lowerScale - 0.1;
    addCap(parts, input, "lower-tower", lowerScale, podiumHeight + lowerHeight - 0.2);
    addFacadeAssembly(
      parts,
      input,
      source,
      "upper-tower",
      upperScale,
      podiumHeight + lowerHeight,
      towerTop,
      "glass",
      "stone",
    );
  }
  parts.push(
    part(
      "box",
      "metal",
      "crown",
      [input.x, towerTop + (crownHeight - 0.2) / 2, input.z],
      [input.width * lowerScale * 0.35, crownHeight - 0.2, input.depth * lowerScale * 0.35],
    ),
  );
  addCap(parts, input, "crown", lowerScale * 0.35, input.height - 0.2);
  return { archetype: "tower", variant, parts };
}

function projectMetreUvs(geometry: THREE.BufferGeometry): void {
  const position = geometry.getAttribute("position");
  const normal = geometry.getAttribute("normal");
  const uv = new THREE.BufferAttribute(new Float32Array(position.count * 2), 2);
  for (let index = 0; index < position.count; index += 1) {
    const x = position.getX(index);
    const y = position.getY(index);
    const z = position.getZ(index);
    const normalX = Math.abs(normal.getX(index));
    const normalY = Math.abs(normal.getY(index));
    const normalZ = Math.abs(normal.getZ(index));
    if (normalY >= normalX && normalY >= normalZ) uv.setXY(index, x, z);
    else if (normalX >= normalZ) uv.setXY(index, z, y);
    else uv.setXY(index, x, y);
  }
  geometry.setAttribute("uv", uv);
}

function footprintGeometry(
  input: BuildingModelInput,
  scale: number,
  height: number,
): THREE.BufferGeometry {
  const points = scaledFootprint(normalizedFootprint(input), input, scale);
  const shape = new THREE.Shape();
  points.forEach(([x, z], index) => {
    if (index === 0) shape.moveTo(x, -z);
    else shape.lineTo(x, -z);
  });
  const geometry = new THREE.ExtrudeGeometry(shape, { depth: height, bevelEnabled: false });
  geometry.rotateX(-Math.PI / 2);
  geometry.computeVertexNormals();
  projectMetreUvs(geometry);
  return geometry;
}

function gableGeometry(part: BuildingPart): THREE.BufferGeometry {
  const [width, height, depth] = part.size;
  const ridgeAlongX = width >= depth;
  const vertices = ridgeAlongX
    ? [
        -width / 2,
        -height / 2,
        -depth / 2,
        width / 2,
        -height / 2,
        -depth / 2,
        -width / 2,
        -height / 2,
        depth / 2,
        width / 2,
        -height / 2,
        depth / 2,
        -width / 2,
        height / 2,
        0,
        width / 2,
        height / 2,
        0,
      ]
    : [
        -width / 2,
        -height / 2,
        -depth / 2,
        width / 2,
        -height / 2,
        -depth / 2,
        -width / 2,
        -height / 2,
        depth / 2,
        width / 2,
        -height / 2,
        depth / 2,
        0,
        height / 2,
        -depth / 2,
        0,
        height / 2,
        depth / 2,
      ];
  const indices = ridgeAlongX
    ? [0, 2, 1, 1, 2, 3, 0, 1, 4, 1, 5, 4, 2, 4, 3, 3, 4, 5, 0, 4, 2, 1, 3, 5]
    : [0, 2, 1, 1, 2, 3, 0, 1, 4, 1, 3, 4, 2, 5, 3, 3, 5, 4, 0, 4, 2, 2, 4, 5];
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(vertices, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  const expanded = geometry.toNonIndexed();
  geometry.dispose();
  expanded.computeVertexNormals();
  projectMetreUvs(expanded);
  return expanded;
}

function geometryForPart(
  input: BuildingModelInput,
  buildingPart: BuildingPart,
): THREE.BufferGeometry {
  let geometry: THREE.BufferGeometry;
  if (buildingPart.kind === "footprint") {
    geometry = footprintGeometry(input, buildingPart.footprintScale ?? 1, buildingPart.size[1]);
  } else if (buildingPart.kind === "gable") {
    geometry = gableGeometry(buildingPart);
  } else {
    geometry = new THREE.BoxGeometry(...buildingPart.size);
    projectRoundedBoxUvs(geometry);
  }
  if (buildingPart.kind === "footprint") {
    geometry.translate(0, buildingPart.center[1] - buildingPart.size[1] / 2, 0);
  } else {
    geometry.rotateY(buildingPart.yawRadians);
    geometry.translate(...buildingPart.center);
  }
  return geometry;
}

function compatibleGeometry(geometry: THREE.BufferGeometry): THREE.BufferGeometry {
  const compatible = geometry.index ? geometry.toNonIndexed() : geometry;
  for (const name of Object.keys(compatible.attributes)) {
    if (name !== "position" && name !== "normal" && name !== "uv") compatible.deleteAttribute(name);
  }
  return compatible;
}

const WINDOW_MASK_SIZE = 64;
const WINDOW_OCCUPANCIES = [0.45, 0.55, 0.65, 0.75, 0.6] as const;
const WINDOW_BRIGHTNESS = [160, 208, 255] as const;
const WINDOW_EMISSIVE_COLORS = [0xffd18a, 0xffe2ad, 0xf2b879, 0xd9e6ff, 0xffcf92] as const;

function createWindowEmissiveMask(variant: number): THREE.DataTexture {
  const pixels = new Uint8Array(WINDOW_MASK_SIZE * WINDOW_MASK_SIZE * 4);
  for (let pixel = 0; pixel < WINDOW_MASK_SIZE * WINDOW_MASK_SIZE; pixel += 1)
    pixels[pixel * 4 + 3] = 255;
  const cells: { lit: boolean; brightness: number }[] = [];
  for (let row = 0; row < 4; row += 1) {
    for (let column = 0; column < 4; column += 1) {
      const hash = buildingSeedForPosition(variant * 17 + column, variant * 31 + row);
      cells.push({
        lit: hash / 0x1_0000_0000 < WINDOW_OCCUPANCIES[variant]!,
        brightness: WINDOW_BRIGHTNESS[(hash >>> 8) % WINDOW_BRIGHTNESS.length]!,
      });
    }
  }
  const litCount = cells.filter((cell) => cell.lit).length;
  if (litCount === 0) cells[0]!.lit = true;
  else if (litCount === cells.length) cells[0]!.lit = false;
  for (let index = 0; index < cells.length; index += 1) {
    const cell = cells[index]!;
    if (!cell.lit) continue;
    const column = index % 4;
    const row = Math.floor(index / 4);
    for (let y = row * 16 + 2; y < (row + 1) * 16 - 2; y += 1) {
      for (let x = column * 16 + 2; x < (column + 1) * 16 - 2; x += 1) {
        const offset = (y * WINDOW_MASK_SIZE + x) * 4;
        pixels[offset] = cell.brightness;
        pixels[offset + 1] = cell.brightness;
        pixels[offset + 2] = cell.brightness;
      }
    }
  }
  const texture = new THREE.DataTexture(
    pixels,
    WINDOW_MASK_SIZE,
    WINDOW_MASK_SIZE,
    THREE.RGBAFormat,
    THREE.UnsignedByteType,
  );
  texture.name = `Buildings:window-emission:${variant}`;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(0.25, 0.25);
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearFilter;
  texture.generateMipmaps = true;
  texture.needsUpdate = true;
  return texture;
}

export class BuildingModelFactory {
  readonly #materialsByRole: Readonly<Record<BuildingMaterialRole, readonly THREE.Material[]>>;
  readonly #materials: ReadonlySet<THREE.Material>;
  readonly #windowEmissiveMasks: readonly THREE.DataTexture[];
  #windowEmissionIntensity = 0;
  #disposed = false;

  constructor(textures: CityTextures) {
    this.#windowEmissiveMasks = WINDOW_OCCUPANCIES.map((_, variant) =>
      createWindowEmissiveMask(variant),
    );
    const surfaceMaterials = (
      role: "brick" | "stone" | "roof",
      roughness: number,
      metalness: number,
      normalScale: number,
    ): THREE.MeshStandardMaterial[] =>
      textures[role].colors.map((map, variant) => {
        const material = new THREE.MeshStandardMaterial({
          name: `Buildings:${role}:${variant}`,
          map,
          normalMap: textures[role].normal,
          roughnessMap: textures[role].roughness,
          roughness,
          metalness,
        });
        material.normalScale.setScalar(normalScale);
        return material;
      });
    const glass = textures.glass.colors.map(
      (map, variant) =>
        new THREE.MeshPhysicalMaterial({
          name: `Buildings:glass:${variant}`,
          map,
          roughnessMap: textures.glass.roughness,
          transmissionMap: textures.glass.transmission,
          emissiveMap: this.#windowEmissiveMasks[variant],
          emissive: WINDOW_EMISSIVE_COLORS[variant],
          emissiveIntensity: 0,
          roughness: 0.12,
          metalness: 0,
          transmission: 0.72,
          thickness: 0.08,
          ior: 1.46,
          specularIntensity: 0.9,
          clearcoat: 1,
          clearcoatRoughness: 0.08,
          opacity: 1,
        }),
    );
    const metal = new THREE.MeshStandardMaterial({
      name: "Buildings:metal:0",
      color: 0x465054,
      roughness: 0.48,
      metalness: 0.55,
    });
    this.#materialsByRole = {
      brick: surfaceMaterials("brick", 0.95, 0, 0.35),
      stone: surfaceMaterials("stone", 0.88, 0.02, 0.22),
      glass,
      roof: surfaceMaterials("roof", 0.9, 0.02, 0.3),
      metal: [metal],
    };
    this.#materials = new Set(Object.values(this.#materialsByRole).flat());
  }

  get materials(): ReadonlySet<THREE.Material> {
    return this.#materials;
  }

  get windowEmissionIntensity(): number {
    return this.#windowEmissionIntensity;
  }

  setArtificialLight(value: number): void {
    this.#windowEmissionIntensity = 1.65 * THREE.MathUtils.clamp(value, 0, 1);
    for (const material of this.#materialsByRole.glass)
      (material as THREE.MeshPhysicalMaterial).emissiveIntensity = this.#windowEmissionIntensity;
  }

  createTile(buildings: readonly BuildingModelInput[]): BuildingTileModel {
    if (this.#disposed) throw new Error("BuildingModelFactory is disposed");
    const root = new THREE.Group();
    const buckets = new Map<
      string,
      { role: BuildingMaterialRole; variant: number; parts: THREE.BufferGeometry[] }
    >();
    const roles: Record<BuildingMaterialRole, number> = {
      brick: 0,
      stone: 0,
      glass: 0,
      roof: 0,
      metal: 0,
    };
    let partCount = 0;
    const completed: THREE.BufferGeometry[] = [];
    try {
      for (const input of buildings) {
        const composition = deriveBuildingComposition(input);
        partCount += composition.parts.length;
        for (const buildingPart of composition.parts) {
          roles[buildingPart.role] += 1;
          const variant = buildingPart.role === "metal" ? 0 : composition.variant;
          const key = `${buildingPart.role}:${variant}`;
          let bucket = buckets.get(key);
          if (!bucket) {
            bucket = { role: buildingPart.role, variant, parts: [] };
            buckets.set(key, bucket);
          }
          bucket.parts.push(geometryForPart(input, buildingPart));
        }
      }
      for (const { role, variant, parts } of buckets.values()) {
        const compatible = parts.map(compatibleGeometry);
        let merged: THREE.BufferGeometry | null = null;
        try {
          merged = mergeGeometries(compatible, false);
        } finally {
          for (const geometry of compatible) geometry.dispose();
          for (const geometry of parts) if (!compatible.includes(geometry)) geometry.dispose();
        }
        if (!merged) throw new Error(`Failed to merge building geometry: ${role}:${variant}`);
        completed.push(merged);
        const material = this.#materialsByRole[role][variant];
        if (!material) throw new Error(`Missing building material: ${role}:${variant}`);
        const mesh = new THREE.Mesh(merged, material);
        mesh.name = `Buildings:${role}:${variant}`;
        mesh.castShadow = role !== "glass";
        mesh.receiveShadow = true;
        root.add(mesh);
      }
      return {
        root,
        stats: {
          buildings: buildings.length,
          parts: partCount,
          meshes: root.children.length,
          roles,
        },
      };
    } catch (error) {
      for (const bucket of buckets.values())
        for (const geometry of bucket.parts) geometry.dispose();
      for (const geometry of completed) geometry.dispose();
      throw error;
    }
  }

  async createTileIncremental(
    buildings: readonly BuildingModelInput[],
    checkpoint: () => Promise<void>,
    signal: AbortSignal,
  ): Promise<BuildingTileModel> {
    if (this.#disposed) throw new Error("BuildingModelFactory is disposed");
    const root = new THREE.Group();
    const buckets = new Map<
      string,
      { role: BuildingMaterialRole; variant: number; parts: THREE.BufferGeometry[] }
    >();
    const roles: Record<BuildingMaterialRole, number> = {
      brick: 0,
      stone: 0,
      glass: 0,
      roof: 0,
      metal: 0,
    };
    const pending = new Set<THREE.BufferGeometry>();
    const completed: THREE.BufferGeometry[] = [];
    let partCount = 0;
    try {
      for (const input of buildings) {
        signal.throwIfAborted();
        const composition = deriveBuildingComposition(input);
        partCount += composition.parts.length;
        for (const buildingPart of composition.parts) {
          roles[buildingPart.role] += 1;
          const variant = buildingPart.role === "metal" ? 0 : composition.variant;
          const key = `${buildingPart.role}:${variant}`;
          let bucket = buckets.get(key);
          if (!bucket) {
            bucket = { role: buildingPart.role, variant, parts: [] };
            buckets.set(key, bucket);
          }
          const geometry = geometryForPart(input, buildingPart);
          bucket.parts.push(geometry);
          pending.add(geometry);
        }
        await checkpoint();
      }
      for (const { role, variant, parts } of buckets.values()) {
        const chunks: THREE.BufferGeometry[] = [];
        for (let start = 0; start < parts.length; start += 128) {
          signal.throwIfAborted();
          const source = parts.slice(start, start + 128);
          const compatible = source.map(compatibleGeometry);
          let chunk: THREE.BufferGeometry | null = null;
          try {
            chunk = mergeGeometries(compatible, false);
          } finally {
            for (const geometry of compatible) geometry.dispose();
            for (const geometry of source) {
              if (!compatible.includes(geometry)) geometry.dispose();
              pending.delete(geometry);
            }
          }
          if (!chunk) throw new Error(`Failed to merge building geometry: ${role}:${variant}`);
          chunks.push(chunk);
          completed.push(chunk);
          await checkpoint();
        }
        const material = this.#materialsByRole[role][variant];
        if (!material) throw new Error(`Missing building material: ${role}:${variant}`);
        let mesh: THREE.Mesh | THREE.BatchedMesh;
        if (chunks.length === 1) {
          mesh = new THREE.Mesh(chunks[0]!, material);
        } else {
          const vertexCapacity = chunks.reduce(
            (count, chunk) => count + chunk.getAttribute("position").count,
            0,
          );
          const batched = new THREE.BatchedMesh(chunks.length, vertexCapacity, 0, material);
          completed.push(batched.geometry);
          for (const chunk of chunks) {
            const geometryId = batched.addGeometry(chunk);
            batched.addInstance(geometryId);
            chunk.dispose();
            completed.splice(completed.indexOf(chunk), 1);
            await checkpoint();
          }
          mesh = batched;
        }
        mesh.name = `Buildings:${role}:${variant}`;
        mesh.castShadow = role !== "glass";
        mesh.receiveShadow = true;
        root.add(mesh);
        await checkpoint();
      }
      signal.throwIfAborted();
      return {
        root,
        stats: {
          buildings: buildings.length,
          parts: partCount,
          meshes: root.children.length,
          roles,
        },
      };
    } catch (error) {
      for (const geometry of pending) geometry.dispose();
      for (const geometry of completed) geometry.dispose();
      root.clear();
      throw error;
    }
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    for (const material of this.#materials) material.dispose();
    for (const texture of this.#windowEmissiveMasks) texture.dispose();
  }
}
